// src/TabbableEffects.tsx
import { useEffect } from "react";
import {
  findNodePath,
  focusEditor,
  getPluginOptions,
  toDOMNode,
  toSlateNode,
  useEditorReadOnly,
  useEditorState
} from "@udecode/plate-common";
import { Path as Path2 } from "slate";
import { tabbable } from "tabbable";

// src/constants.ts
var KEY_TABBABLE = "tabbable";

// src/findTabDestination.ts
import {
  getPoint,
  getPointAfter
} from "@udecode/plate-common";
import { Path } from "slate";
var findTabDestination = (editor, { tabbableEntries, activeTabbableEntry, direction }) => {
  var _a, _b;
  if (activeTabbableEntry) {
    const activeTabbableEntryIndex = tabbableEntries.indexOf(activeTabbableEntry);
    const nextTabbableEntryIndex = activeTabbableEntryIndex + (direction === "forward" ? 1 : -1);
    const nextTabbableEntry2 = tabbableEntries[nextTabbableEntryIndex];
    if (nextTabbableEntry2 && Path.equals(activeTabbableEntry.path, nextTabbableEntry2.path)) {
      return {
        type: "dom-node",
        domNode: nextTabbableEntry2.domNode
      };
    }
    if (direction === "forward") {
      const pointAfter = getPointAfter(editor, activeTabbableEntry.path);
      if (!pointAfter)
        return null;
      return {
        type: "path",
        path: pointAfter.path
      };
    }
    return {
      type: "path",
      path: getPoint(editor, activeTabbableEntry.path).path
    };
  }
  const selectionPath = ((_b = (_a = editor.selection) == null ? void 0 : _a.anchor) == null ? void 0 : _b.path) || [];
  const nextTabbableEntry = direction === "forward" ? tabbableEntries.find(
    (entry) => !Path.isBefore(entry.path, selectionPath)
  ) : [...tabbableEntries].reverse().find((entry) => Path.isBefore(entry.path, selectionPath));
  if (nextTabbableEntry) {
    return {
      type: "dom-node",
      domNode: nextTabbableEntry.domNode
    };
  }
  return null;
};

// src/TabbableEffects.tsx
function TabbableEffects() {
  const editor = useEditorState();
  const readOnly = useEditorReadOnly();
  const { query, globalEventListener, insertTabbableEntries, isTabbable } = getPluginOptions(editor, KEY_TABBABLE);
  useEffect(() => {
    if (readOnly)
      return;
    const editorDOMNode = toDOMNode(editor, editor);
    if (!editorDOMNode)
      return;
    const handler = (event) => {
      var _a;
      if (event.key !== "Tab" || event.defaultPrevented || !(query == null ? void 0 : query(editor, event))) {
        return;
      }
      const insertedTabbableEntries = insertTabbableEntries == null ? void 0 : insertTabbableEntries(
        editor,
        event
      );
      if (globalEventListener && event.target && ![
        editorDOMNode,
        ...insertedTabbableEntries.map(({ domNode }) => domNode)
      ].some((container) => container.contains(event.target))) {
        return;
      }
      const tabbableDOMNodes = tabbable(editorDOMNode);
      const defaultTabbableEntries = tabbableDOMNodes.map((domNode) => {
        const slateNode = toSlateNode(editor, domNode);
        if (!slateNode)
          return;
        return {
          domNode,
          slateNode,
          path: findNodePath(editor, slateNode)
        };
      }).filter(
        (entry) => entry && (isTabbable == null ? void 0 : isTabbable(editor, entry))
      );
      const tabbableEntries = [
        ...insertedTabbableEntries,
        ...defaultTabbableEntries
      ].sort((a, b) => Path2.compare(a.path, b.path));
      const { activeElement } = document;
      const activeTabbableEntry = (_a = activeElement && tabbableEntries.find((entry) => entry.domNode === activeElement)) != null ? _a : null;
      const tabDestination = findTabDestination(editor, {
        tabbableEntries,
        activeTabbableEntry,
        direction: event.shiftKey ? "backward" : "forward"
      });
      if (tabDestination) {
        event.preventDefault();
        switch (tabDestination.type) {
          case "path": {
            focusEditor(editor, {
              anchor: { path: tabDestination.path, offset: 0 },
              focus: { path: tabDestination.path, offset: 0 }
            });
            break;
          }
          case "dom-node": {
            tabDestination.domNode.focus();
            break;
          }
        }
        return;
      }
      tabbableDOMNodes.forEach((domNode) => {
        const oldTabIndex = domNode.getAttribute("tabindex");
        domNode.setAttribute("tabindex", "-1");
        setTimeout(() => {
          if (oldTabIndex) {
            domNode.setAttribute("tabindex", oldTabIndex);
          } else {
            domNode.removeAttribute("tabindex");
          }
        }, 0);
      });
    };
    const eventListenerNode = globalEventListener ? document.body : editorDOMNode;
    eventListenerNode.addEventListener("keydown", handler, true);
    return () => eventListenerNode.removeEventListener("keydown", handler, true);
  }, [
    readOnly,
    editor,
    globalEventListener,
    isTabbable,
    insertTabbableEntries,
    query
  ]);
  return null;
}

// src/createTabbablePlugin.ts
import { createPluginFactory, isVoid } from "@udecode/plate-common";
var createTabbablePlugin = createPluginFactory({
  key: KEY_TABBABLE,
  renderAfterEditable: TabbableEffects,
  options: {
    query: () => true,
    globalEventListener: false,
    insertTabbableEntries: () => [],
    isTabbable: (editor, tabbableEntry) => isVoid(editor, tabbableEntry.slateNode)
  }
});
export {
  KEY_TABBABLE,
  TabbableEffects,
  createTabbablePlugin,
  findTabDestination
};
//# sourceMappingURL=index.mjs.map